import { useEffect, useState } from "react";
import { collection, addDoc, updateDoc, doc, getDoc, serverTimestamp, getFirestore, deleteField } from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { PlusIcon } from "@heroicons/react/24/solid"; // import heroicons at top
import FormResponseModal from "./FormResponsesModal";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { fetchMySubGroups, fetchTargetedSubGroups } from "../../services/firestore/SGloopService";
import ConfirmModal from "./ConfirmModal";


const questionTypes = ["text", "textarea", "multiple choice", "checkbox"];

const FormsModal = ({ open, setOpen, editingForm, setEditingForm }) => {
  const db = getFirestore(firebaseApp);
  const { userData, userRef } = useAuth();
  const [banner, setBanner] = useState(null); // preview URL
  const [bannerFile, setBannerFile] = useState(null); // actual file
  const editingFormId = editingForm?.id;
  const storage = getStorage();
  const [title, setTitle] = useState(editingForm?.title || "");
  const [descriptions, setDescriptions] = useState(editingForm?.descriptions || [""]);
  const [groups, setGroups] = useState(editingForm?.groups || []);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allMySubGroups, setAllMySubGroups] = useState(null); // store cached results
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [benefits, setBenefits] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openClosedModal,setOpenClosedModal] = useState(false);

  useEffect(() => {
    if (editingForm) {
      setTitle(editingForm.title || "");
      setDescriptions(editingForm.descriptions || [""]);
      setGroups(editingForm.groups || []);
      setBanner(editingForm.bannerUrl || null);
      const fetchGroups = async () => {
      if (editingForm?.sgloopGroup) {
        try {
          const subSelectedGroup = await fetchTargetedSubGroups(userRef, editingForm.sgloopGroup.path);
          setSelectedGroup({...subSelectedGroup, ref:{path:editingForm.sgloopGroup.path}});
        } catch (err) {
          console.error("Failed to fetch sub groups:", err);
        }
      }
    };
    fetchGroups();
      if(editingForm?.sgloopBenefits){
        setBenefits(editingForm.sgloopBenefits)
      }
    } else {
      setTitle("");
      setDescriptions([""]);
      setGroups([]);
    }
  }, [editingForm]);

useEffect(() => {
  if (!searchTerm.trim()) {
    setFilteredGroups([]);
    return;
  }

  const timeout = setTimeout(async () => {
    setIsLoading(true);

    // Fetch only once
    if (!allMySubGroups) {
      const mySubGroups = await fetchMySubGroups(userRef);
      setAllMySubGroups(mySubGroups); // cache for filtering later
      setFilteredGroups(
        mySubGroups.filter(g =>
          g.subGroupName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      // Filter cached results
      setFilteredGroups(
        allMySubGroups.filter(g =>
          g.subGroupName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setIsLoading(false);
  }, 400);

  return () => clearTimeout(timeout);
}, [searchTerm]);


  if (!open) return null;


  // -----------------------
  // Group Handlers
  // -----------------------
  const addGroup = (type = "regular") => {
    const newGroup = {
      id: Date.now(),
      type, // 'regular', 'repeatable', 'conditional'
      name: "", // empty by default
      description: [], // new: each group has its own description
      questions: type === "conditional" ? [
        {
          id: Date.now(),
          question: "",
          type: "multiple choice",
          options: ["Option 1"],
          required: false,
          branches: { "Option 1": [] }
        }
      ] : []
    };
    setGroups([...groups, newGroup]);
  };

  const moveGroup = (groupIndex, direction) => {
    const newGroups = [...groups];
    const targetIndex = groupIndex + direction;
    if (targetIndex < 0 || targetIndex >= newGroups.length) return;
    [newGroups[groupIndex], newGroups[targetIndex]] = [newGroups[targetIndex], newGroups[groupIndex]];
    setGroups(newGroups);
  };

  // -----------------------
  // Question Handlers
  // -----------------------
  const addQuestion = (groupId, branchKey = null, parentId = null) => {
    const newQuestion = {
      id: Date.now(),
      question: "",
      type: "text",
      options: [],
      required: false,
      branches: {}
    };

    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      if (group.type === "conditional" && branchKey !== null && parentId !== null) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey].push(newQuestion);
        return { ...group };
      } else {
        return { ...group, questions: [...group.questions, newQuestion] };
      }
    }));
  };

  const moveQuestion = (groupId, questionIndex, direction, branchKey = null, parentId = null) => {
    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      const swap = (arr) => {
        const targetIndex = questionIndex + direction;
        if (targetIndex < 0 || targetIndex >= arr.length) return arr;
        [arr[questionIndex], arr[targetIndex]] = [arr[targetIndex], arr[questionIndex]];
        return arr;
      };

      if (branchKey && parentId) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey] = swap([...parentQuestion.branches[branchKey]]);
      } else {
        group.questions = swap([...group.questions]);
      }

      return { ...group };
    }));
  };

  const handleQuestionChange = (groupId, questionId, value, branchKey = null, parentId = null) => {
    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      const updateQuestion = (q) => q.id === questionId ? { ...q, question: value } : q;

      if (branchKey && parentId) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey] = parentQuestion.branches[branchKey].map(updateQuestion);
      } else {
        group.questions = group.questions.map(updateQuestion);
      }

      return { ...group };
    }));
  };

  const handleTypeChange = (groupId, questionId, type, branchKey = null, parentId = null) => {
    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      const updateQuestion = (q) => {
        if (q.id !== questionId) return q;
        if (type === "text" || type === "textarea") {
          return { ...q, type, options: [], branches: {} };
        } else {
          const newBranches = {};
          (q.options.length ? q.options : ["Option 1"]).forEach(opt => {
            newBranches[opt] = q.branches[opt] || [];
          });
          return { ...q, type, options: q.options.length ? q.options : ["Option 1"], branches: newBranches };
        }
      };

      if (branchKey && parentId) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey] = parentQuestion.branches[branchKey].map(updateQuestion);
      } else {
        group.questions = group.questions.map(updateQuestion);
      }

      return { ...group };
    }));
  };

  const handleOptionChange = (groupId, questionId, optionIdx, value) => {
    setGroups(groups.map(group => {
      const question = group.questions.find(q => q.id === questionId);
      if (!question) return group;

      const oldOption = question.options[optionIdx];
      question.options[optionIdx] = value;

      if (question.branches[oldOption]) {
        question.branches[value] = question.branches[oldOption];
        delete question.branches[oldOption];
      }

      return { ...group };
    }));
  };

  const addOption = (groupId, questionId) => {
    setGroups(groups.map(group => {
      const question = group.questions.find(q => q.id === questionId);
      if (!question) return group;
      const newOption = `Option ${question.options.length + 1}`;
      question.options.push(newOption);
      question.branches[newOption] = [];
      return { ...group };
    }));
  };

  const handleRequiredToggle = (groupId, questionId, branchKey = null, parentId = null) => {
    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      const toggleQuestion = (q) => q.id === questionId ? { ...q, required: !q.required } : q;

      if (branchKey && parentId) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey] = parentQuestion.branches[branchKey].map(toggleQuestion);
      } else {
        group.questions = group.questions.map(toggleQuestion);
      }

      return { ...group };
    }));
  };

  const handleGroupNameChange = (groupId, value) => {
    setGroups(groups.map(g => g.id === groupId ? { ...g, name: value } : g));
  };

  const handleGroupDescriptionChange = (groupId, value) => {
    setGroups(groups.map(g => g.id === groupId ? { ...g, description: value } : g));
  };

  const deleteQuestion = (groupId, questionId, branchKey = null, parentId = null) => {
    setGroups(groups.map(group => {
      if (group.id !== groupId) return group;

      if (branchKey && parentId) {
        const parentQuestion = group.questions.find(q => q.id === parentId);
        parentQuestion.branches[branchKey] = parentQuestion.branches[branchKey].filter(q => q.id !== questionId);
      } else {
        group.questions = group.questions.filter(q => q.id !== questionId);
      }

      return { ...group };
    }));
  };

  const deleteGroup = (groupId) => {
    setGroups(groups.filter(g => g.id !== groupId));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file); // keep file for upload
      setBanner(URL.createObjectURL(file)); // keep preview
    }
  };


  // -----------------------
  // UI RENDER FUNCTIONS
  // -----------------------
  const renderQuestions = (questions, groupId, branchKey = null, parentId = null) => {
    return questions.map((q, qi) => (
      <div key={q.id} className="mb-3 border-b pb-2 ml-0" style={{ marginLeft: branchKey ? 20 : 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
          <input
            type="text"
            value={q.question}
            onChange={(e) => handleQuestionChange(groupId, q.id, e.target.value, branchKey, parentId)}
            className="flex-1 border border-gray-300 rounded px-2 sm:px-3 py-1 sm:py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter question"
          />
          <select
            value={q.type}
            onChange={(e) => handleTypeChange(groupId, q.id, e.target.value, branchKey, parentId)}
            className="w-full sm:w-auto border border-gray-300 rounded px-2 py-1 text-sm sm:text-base focus:outline-none"
          >
            {questionTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <label className="text-sm flex items-center gap-1">
            <input type="checkbox" checked={q.required} onChange={() => handleRequiredToggle(groupId, q.id, branchKey, parentId)} />
            Required
          </label>
          <div className="flex gap-1">
            <button onClick={() => moveQuestion(groupId, qi, -1, branchKey, parentId)} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs">↑</button>
            <button onClick={() => moveQuestion(groupId, qi, 1, branchKey, parentId)} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs">↓</button>
            <button
              onClick={() => deleteQuestion(groupId, q.id, branchKey, parentId)}
              className="px-2 py-1 bg-red-200 text-red-700 rounded hover:bg-red-300 text-xs"
            >
              ✕
            </button>
          </div>
        </div>

        {(q.type === "multiple choice" || q.type === "checkbox") && (
          <>
            {q.options.map((opt, idx) => (
              <div key={idx} className="flex items-center mb-1 gap-2 ml-4">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => handleOptionChange(groupId, q.id, idx, e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-2 sm:px-3 py-1 text-sm focus:outline-none"
                />
              </div>
            ))}
            <button onClick={() => addOption(groupId, q.id)} className="mt-1 px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm bg-gray-200 rounded hover:bg-gray-300 ml-4">
              + Add Option
            </button>
            {Object.entries(q.branches).map(([opt, branchQuestions]) => (
              <div key={opt} className="ml-6 border-l border-gray-300 pl-2 mt-2">
                <p className="text-sm italic mb-1">If "{opt}" →</p>
                {renderQuestions(branchQuestions, groupId, opt, q.id)}
                <button
                  onClick={() => addQuestion(groupId, opt, q.id)}
                  className="mt-1 px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  + Add Question
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    ));
  };

  // -----------------------
  // SAVE / PREVIEW
  // -----------------------
  const handlePreview = () => {
    if (!title.trim()) return alert("Form title is required!");
    sessionStorage.setItem("previewForm", JSON.stringify({ title, descriptions, groups, sgloopGroupData:{selectedGroup}, benefits }));
    window.open("/forms/preview", "_blank");
  };

const handleSave = async () => {
  if (!title.trim()) return alert("Form title is required!");

  try {
    let bannerUrl = null;

    if (editingFormId) {
      const formRef = doc(db, "forms", editingFormId);
      const snap = await getDoc(formRef);
      const currentData = snap.data();
      const newVersion = snap.exists() ? (currentData.version || 1) + 1 : 1;
      bannerUrl = currentData.bannerUrl || null;

      // Handle banner upload
      if (bannerFile) {
        if (currentData.bannerUrl) {
          try {
            await deleteObject(ref(storage, currentData.bannerUrl));
          } catch (err) {
            console.warn(err);
          }
        }
        const storageRef = ref(storage, `forms/${Date.now()}-${bannerFile.name}`);
        await uploadBytes(storageRef, bannerFile);
        bannerUrl = await getDownloadURL(storageRef);
      } else if (banner === null && currentData.bannerUrl) {
        try {
          await deleteObject(ref(storage, currentData.bannerUrl));
        } catch (err) {
          console.warn(err);
        }
        bannerUrl = null;
      }

      // Prepare SGloop reference if selected
      const sgloopRef = selectedGroup ? doc(db, selectedGroup.ref.path) : null;

      const updateData = {
        title,
        descriptions,
        groups,
        bannerUrl: bannerUrl ?? currentData.bannerUrl ?? null,
        version: newVersion,
        status: "Active",
        updatedAt: serverTimestamp(),
        ...(sgloopRef ? { sgloopGroup: sgloopRef } : { sgloopGroup: deleteField() }),
        ...(benefits ? { sgloopBenefits: benefits } : { sgloopBenefits: deleteField() }),
      };

      await updateDoc(formRef, updateData);
      alert(`Form updated (v${newVersion})`);
    } else {
      // New form
      if (bannerFile) {
        const storageRef = ref(storage, `forms/${Date.now()}-${bannerFile.name}`);
        await uploadBytes(storageRef, bannerFile);
        bannerUrl = await getDownloadURL(storageRef);
      }

      const sgloopRef = selectedGroup ? doc(db, selectedGroup.ref.path) : null;

      const newData = {
        title,
        descriptions,
        groups,
        bannerUrl,
        version: 1,
        admin: userData.uid,
        subadmin: [],
        status: "Active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(sgloopRef ? { sgloopGroup: sgloopRef } : {}),
        ...(benefits ? { sgloopBenefits: benefits } : {}),
      };

      await addDoc(collection(db, "forms"), newData);
      alert("Form created (v1)");
    }

    setOpen(false);
    resetValue();
  } catch (err) {
    console.error("Error saving form:", err);
    alert("Failed to save form. Try again.");
  }
};


  const handleClose = async () => {

    try {
      if (editingFormId) {
        const formRef = doc(db, "forms", editingFormId);
       
        await updateDoc(formRef, {
          status: "Closed",
          updatedAt: serverTimestamp(),
        }).then(() => {
          alert(`Form closed`);
          setOpenClosedModal(false)
        });
      }

      setOpen(false);
      resetValue();
    } catch (err) {
      console.error("Error saving form:", err);
      alert("Failed to save form. Try again.");
    }
  };

  const resetValue = () => {
    setTitle("");
    setDescriptions([""]);
    setGroups([]);
    setSelectedGroup(null);
    setBanner(null);
    setBannerFile(null);
    setEditingForm(null);
  }


  // -----------------------
  // RENDER
  // -----------------------
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      {openClosedModal && (<ConfirmModal 
      onClose={() => setOpenClosedModal(false)}
        onConfirm={handleClose}
        confirmTitle = "Close Form"
        confirmSubtitle = "Confirming will close the form and make it no longer publicly accessible."
      ></ConfirmModal>)}
      <div className="bg-white w-full max-w-full sm:max-w-4xl mx-2 sm:mx-4 p-4 sm:p-6 rounded shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-bold">Create Form</h2>
          <div className="flex items-center gap-2">
            {/* <button
              onClick={() => setResponseModalOpen(true)}
              className="text-blue-600 hover:text-blue-800 text-sm sm:text-base"
            >
              View Responses
            </button> */}
            <XMarkIcon
              className="w-5 h-5"
              onClick={() => {
                setOpen(false);
                resetValue();
              }}></XMarkIcon>  </div>
        </div>

        {/* Form Title & Description */}
        <div className="mb-6 sm:mb-8">
          {/* Upload Banner Section */}
          <div className="mb-4">
            {banner ? (
              <div className="relative">
                <img
                  src={banner}
                  alt="Form banner"
                  className="w-full max-h-60 object-contain rounded-lg border bg-gray-100"
                />
                <button
                  type="button"
                  onClick={() => setBanner(null)}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                <PhotoIcon className="w-8 h-8 text-gray-400" />
                <span className="mt-1 text-sm text-gray-500">Upload Banner</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Form Title */}
          <label className="block font-medium mb-1 text-sm sm:text-base">
            Form Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 sm:px-3 py-1.5 sm:py-2 mb-3 sm:mb-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter form title"
          />

          <label className="block font-medium mb-1 text-sm sm:text-base">Form Description (optional)</label>

          {descriptions.map((desc, idx) => (
            <textarea
              key={idx}
              value={desc}
              onChange={(e) => {
                const newDesc = [...descriptions];
                newDesc[idx] = e.target.value;
                setDescriptions(newDesc);
              }}
              className="w-full border border-gray-300 rounded px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add a description..."
            />
          ))}

          <button
            type="button"
            onClick={() => setDescriptions([...descriptions, ""])}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
          >
            <PlusIcon className="w-4 h-4" /> Add Description
          </button>

          <div className="mt-2 flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
            <button onClick={() => addGroup("regular")} className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm">+ Add Section</button>
            <button onClick={() => addGroup("repeatable")} className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm">+ Add Repeatable Section</button>
            <button onClick={() => addGroup("conditional")} className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm">+ Add Conditional Section</button>
          </div>
        </div>

        {/* Groups */}
        {groups.map((g, gi) => (
          <div key={g.id} className="mb-6 border p-3 rounded shadow-sm bg-gray-50">
            <div className="flex items-center justify-between mb-2 gap-2">
              <input
                type="text"
                value={g.name}
                onChange={(e) => handleGroupNameChange(g.id, e.target.value)}
                placeholder="Enter section title"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-1">
                <button onClick={() => moveGroup(gi, -1)} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs">↑</button>
                <button onClick={() => moveGroup(gi, 1)} className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 text-xs">↓</button>
                <button
                  onClick={() => deleteGroup(g.id)}
                  className="px-2 py-1 bg-red-200 text-red-700 rounded hover:bg-red-300 text-xs"
                >
                  ✕
                </button>
              </div>
            </div>
            <textarea
              value={g.description}
              onChange={(e) => handleGroupDescriptionChange(g.id, e.target.value)}
              placeholder="Section description (optional)"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm sm:text-base mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {renderQuestions(g.questions, g.id)}
            {g.type !== "conditional" && (
              <button onClick={() => addQuestion(g.id)} className="mt-2 px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm">+ Add Question</button>
            )}
          </div>
        ))}
        <div className="border-t border-gray-200 my-4"></div>
        {/* Link to SGloop Group */}
        <div className="mb-6">
  <h3 className="text-lg font-semibold mb-1">Optional: Link to Your SGloop Group</h3>
  <p className="text-sm text-gray-500 mb-3">
    You can optionally link this form to one of your SGloop groups. Start typing below to search and select a group. Providing this information is not required.
  </p>

  {/* Search Input */}
  <div className="relative">
    <input
      type="text"
      placeholder="Search for your SGloop group (optional)..."
      value={searchTerm}
      onChange={(e) => {
        setSearchTerm(e.target.value);
        setShowDropdown(true);
      }}
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    {showDropdown && searchTerm.trim() !== "" && (
      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow max-h-40 overflow-y-auto">
        {isLoading ? (
          <p className="p-2 text-gray-500 text-sm">Searching...</p>
        ) : filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => {
                setSelectedGroup(group);
                setShowDropdown(false);
              }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
            >
              {group.subGroupName}
            </button>
          ))
        ) : (
          <p className="p-2 text-gray-500 text-sm">No groups found</p>
        )}
      </div>
    )}
  </div>

  {/* Selected Group */}
  {selectedGroup && (
    <div className="mt-4 border rounded-lg p-3 bg-gray-50">
      <div className="flex justify-between items-center">
        <h4 className="font-medium text-sm sm:text-base">{selectedGroup.subGroupName}</h4>
        <button
          onClick={() => setSelectedGroup(null)}
          className="text-xs text-red-600 hover:underline"
        >
          Remove
        </button>
      </div>

      {/* Benefits Textarea */}
      <label className="block mt-3 mb-1 text-sm text-gray-700 font-medium">
        Benefits of Joining (optional)
      </label>
      <textarea
        value={benefits}
        onChange={(e) => setBenefits(e.target.value)}
        placeholder="Describe the benefits of joining this group..."
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows={3}
      ></textarea>
    </div>
  )}
</div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-6 sm:mt-8">
          <button onClick={() => setOpen(false)} className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
          {(!editingForm || editingForm.status !== "Closed") && (
            <>
              <button
                onClick={handlePreview}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Preview
              </button>

              <button
                onClick={handleSave}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-green-600 text-white rounded hover:bg-green-700"
              >
                {editingForm ? "Update" : "Save"} Form
              </button>

              {editingForm && (<button
                onClick={() => setOpenClosedModal(true)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-red-600 text-white rounded hover:bg-red-700"
              >
                Closed Form
              </button>)}
            </>
          )}
          
        </div>
      </div>
      <FormResponseModal open={responseModalOpen} setOpen={setResponseModalOpen} formId={editingFormId} editingForm={editingForm} />

    </div>
  );
};

export default FormsModal;
